import type { Message, ToolCall } from '@copilotkit/shared';

export const ToolDisplayStatus = {
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
  NEEDS_APPROVAL: 'needs-approval',
  REJECTED: 'rejected',
} as const;

export type ToolDisplayStatus =
  (typeof ToolDisplayStatus)[keyof typeof ToolDisplayStatus];

/**
 * Serializable message type for Redux storage
 *
 * This removes non-serializable properties from CopilotKit's Message type:
 * - generativeUI: Runtime function that renders tool calls
 * - state: Complex state objects that may contain non-serializable data
 *
 * All other properties (including image, agentName, toolName) are preserved
 * as they are serializable.
 */
export type SerializableMessage = Omit<Message, 'generativeUI' | 'state'>;

/**
 * Content item in an array-based message content
 */
export type MessageContentItem = {
  type: 'text';
  text: string;
};

/**
 * CopilotKit extends AG-UI messages with additional runtime properties
 * that are not included in the base type definitions
 */
export type CopilotKitMessage = Message & {
  /**
   * Optional function that renders tool calls or other generative UI
   * Added by CopilotKit at runtime for messages with tool calls
   */
  generativeUI?: () => React.ReactElement | null;
  toolCalls?: ToolCall[];
};

/**
 * Type guard to check if a message is a user message
 */
export const isUserMessage = (
  message: CopilotKitMessage
): message is CopilotKitMessage & { role: 'user' } => {
  return message.role === 'user';
};

/**
 * Safely extracts text content from a message, handling both string and array formats
 */
export const getMessageContent = (message: CopilotKitMessage): string => {
  // Check if message has content property (not all message types do)
  if (!('content' in message)) {
    return '';
  }

  const content = message.content;

  if (typeof content === 'string') {
    return content;
  }

  if (Array.isArray(content)) {
    return content
      .filter((item: MessageContentItem) => item.type === 'text')
      .map((item: MessageContentItem) => item.text || '')
      .join('\n');
  }

  return '';
};
